import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { IGNORE_KEY, SectorItem, categoryColor, useSectorMapping } from '../../hooks/event-comparison'
import { SERIES_BASE, SERIES_COMPARE } from './styles'
import SectorMappingModal from './SectorMappingModal'

const INK = '#212121'
const MUTED = '#555555'
const FAINT = '#B0B0B0'
const BORDER = '#EAECF0'

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 24px 0 12px;
`
const Kicker = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${FAINT};
  text-transform: uppercase;
`
const Name = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: ${INK};
  margin: 0;
`
const Card = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  padding: 18px;
`
const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
const CardTitle = styled.div`
  font-size: 14.5px;
  font-weight: 660;
  color: ${INK};
`
const CardSub = styled.div`
  font-size: 11.5px;
  color: ${FAINT};
  margin: 2px 0 14px;
`
const MapBtn = styled.button`
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: #B0020B;
  background: rgba(52, 86, 218, 0.1);
  border: 1px solid rgba(52, 86, 218, 0.3);
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;
  &:hover {
    background: rgba(52, 86, 218, 0.18);
  }
`
const HRow = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  & + & {
    border-top: 1px solid #f2f4f7;
  }
`
const HLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${INK};
  display: inline-flex;
  align-items: center;
  gap: 7px;
`
const CatDot = styled.span<{ $c: string }>`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: ${(p) => p.$c};
  flex: none;
`
const HBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const HTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const HFill = styled.div<{ $w: number; $c: string }>`
  height: 9px;
  border-radius: 5px;
  min-width: 2px;
  background: ${(p) => p.$c};
  width: ${(p) => Math.max(p.$w, 0)}%;
`
const HPct = styled.span<{ $c: string }>`
  font-size: 12px;
  font-weight: 660;
  color: ${(p) => p.$c};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
const LegendMini = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  font-size: 11.5px;
  color: ${MUTED};
  margin-top: 12px;
`
const LDot = styled.span<{ $c: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$c};
  margin-right: 6px;
`
const Empty = styled.div`
  font-size: 12.5px;
  color: ${FAINT};
  padding: 8px 0;
`
const Note = styled.div`
  font-size: 11px;
  color: ${FAINT};
  margin-top: 14px;
  border-top: 1px dashed ${BORDER};
  padding-top: 12px;
`

interface Props {
  baseName: string
  compareName: string
  baseEventIds: number[]
  compareEventIds: number[]
  baseSectors: SectorItem[]
  compareSectors: SectorItem[]
}

/** Seção COMERCIAL — comparativo de setores por CATEGORIA CANÔNICA (OP-3415 Fase B).
 *  Cada evento mapeia seus setores reais para categorias (Pista/Premium/…); a
 *  comparação agrega por categoria, com sugestão automática e edição no modal. */
const SetoresComparison: FC<Props> = ({
  baseName,
  compareName,
  baseEventIds,
  compareEventIds,
  baseSectors,
  compareSectors,
}) => {
  const [openModal, setOpenModal] = useState(false)
  const baseMap = useSectorMapping(baseEventIds)
  const compareMap = useSectorMapping(compareEventIds)

  const baseMapping = baseMap.mapping
  const compareMapping = compareMap.mapping

  // cor/rótulo por categoria: usa o que o backend mandou; cai na cor determinística.
  const catColor = (key: string) => {
    const fromApi =
      baseMapping?.categories.find((c) => c.key === key)?.color ||
      compareMapping?.categories.find((c) => c.key === key)?.color
    return fromApi || categoryColor(key)
  }

  // agrega o % de ingressos (na janela) por categoria (rótulo livre) de cada evento
  const grouped = useMemo(() => {
    const catOf = (m: typeof baseMapping, id: number): string | undefined =>
      m?.sectors.find((s) => s.id === id)?.category
    const sum = (sectors: SectorItem[], m: typeof baseMapping) => {
      const acc: Record<string, number> = {}
      sectors.forEach((s) => {
        const cat = catOf(m, s.id)
        if (cat && cat !== IGNORE_KEY) acc[cat] = (acc[cat] || 0) + s.pct
      })
      return acc
    }
    const a = sum(baseSectors, baseMapping)
    const b = sum(compareSectors, compareMapping)
    // universo = todas as categorias mapeadas nos dois eventos (não só as que
    // venderam na janela) — assim o comparativo mostra a lista completa.
    const universe = new Set<string>([...Object.keys(a), ...Object.keys(b)])
    ;(baseMapping?.categories || []).forEach((c) => c.key !== IGNORE_KEY && universe.add(c.key))
    ;(compareMapping?.categories || []).forEach((c) => c.key !== IGNORE_KEY && universe.add(c.key))
    const rows = Array.from(universe)
      .map((cat) => ({ cat, a: a[cat] || 0, b: b[cat] || 0 }))
      .sort((x, y) => Math.max(y.a, y.b) - Math.max(x.a, x.b))
    const max = Math.max(1, ...rows.flatMap((r) => [r.a, r.b]))
    return { rows, max }
  }, [baseSectors, compareSectors, baseMapping, compareMapping])

  const ready = baseEventIds.length > 0 && compareEventIds.length > 0
  const anyLoading = baseMap.loading || compareMap.loading

  return (
    <>
      <SectionHead>
        <Kicker>Comercial</Kicker>
        <Name>Setores / tipos de ingresso</Name>
      </SectionHead>

      <Card>
        <CardHead>
          <CardTitle>Mix por categoria de setor</CardTitle>
          {ready && (
            <MapBtn type="button" onClick={() => setOpenModal(true)}>
              ⚙ Revisar mapeamento
            </MapBtn>
          )}
        </CardHead>
        <CardSub>% dos ingressos por categoria equivalente · Base × Comparado</CardSub>

        {grouped.rows.length === 0 ? (
          <Empty>{anyLoading ? 'Carregando setores…' : 'Sem setores com venda na janela.'}</Empty>
        ) : (
          <>
            <div>
              {grouped.rows.map((r) => (
                <HRow key={r.cat}>
                  <HLabel title={r.cat}>
                    <CatDot $c={catColor(r.cat)} /> {r.cat}
                  </HLabel>
                  <HBars>
                    <HTrack>
                      <HFill $w={(r.a / grouped.max) * 100} $c={SERIES_BASE} />
                      <HPct $c={SERIES_BASE}>{r.a.toFixed(0)}%</HPct>
                    </HTrack>
                    <HTrack>
                      <HFill $w={(r.b / grouped.max) * 100} $c={SERIES_COMPARE} />
                      <HPct $c={SERIES_COMPARE}>{r.b.toFixed(0)}%</HPct>
                    </HTrack>
                  </HBars>
                </HRow>
              ))}
            </div>
            <LegendMini>
              <span><LDot $c={SERIES_BASE} />{baseName}</span>
              <span><LDot $c={SERIES_COMPARE} />{compareName}</span>
            </LegendMini>
          </>
        )}

        <Note>
          Categorias sugeridas pelo nome do setor (2 primeiras palavras). Use <b>Revisar mapeamento</b> para
          renomear, criar ou ajustar quais setores de cada evento entram em cada categoria.
        </Note>
      </Card>

      {openModal && ready && (
        <SectorMappingModal
          baseEventIds={baseEventIds}
          compareEventIds={compareEventIds}
          baseName={baseName}
          compareName={compareName}
          onClose={() => setOpenModal(false)}
          onSaved={() => {
            baseMap.refetch()
            compareMap.refetch()
            setOpenModal(false)
          }}
        />
      )}
    </>
  )
}

export default SetoresComparison
